"use client";

import { getCurrentSession } from "./cognito";

const API_BASE_URL =
	process.env.NEXT_PUBLIC_API_BASE_URL || "https://api.taxlienvest.com";

const HEADER_PUBLIC_ENTITY = "X-Public-Entity-Id";

/**
 * Get the Cognito access token for authenticated API requests.
 */
const getAccessToken = async (): Promise<string | null> => {
	try {
		const session = await getCurrentSession();
		const accessToken = session.getAccessToken();
		return accessToken.getJwtToken();
	} catch (error) {
		console.error("Failed to get access token:", error);
		return null;
	}
};

function headersJson(entityId: string): HeadersInit {
	return {
		"Content-Type": "application/json",
		[HEADER_PUBLIC_ENTITY]: entityId,
	};
}

/** No header — first step in flow */
export async function imageryCheckIn(
	email: string
): Promise<{ enrichmentPublicEntityId: string }> {
	const res = await fetch(`${API_BASE_URL}/imagery/check-in`, {
		method: "POST",
		headers: { "Content-Type": "application/json" },
		body: JSON.stringify({ email }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message || "Imagery check-in failed"
		);
	}
	return (await res.json()) as { enrichmentPublicEntityId: string };
}

export interface ImageryValidatedAddress {
	address: string;
	city: string;
	state: string;
	county?: string;
	zip?: string;
	lat: number;
	lng: number;
	place_name?: string;
}

export async function validateImageryAddress(
	enrichmentPublicEntityId: string,
	address: string
): Promise<ImageryValidatedAddress> {
	const res = await fetch(`${API_BASE_URL}/imagery/address/validate`, {
		method: "POST",
		headers: headersJson(enrichmentPublicEntityId),
		body: JSON.stringify({ address }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message || "Address validation failed"
		);
	}
	return (await res.json()) as ImageryValidatedAddress;
}

export type ImageryCsvRowResult =
	| (ImageryValidatedAddress & { error?: never })
	| { error: string };

export async function validateImageryAddressesCsv(
	enrichmentPublicEntityId: string,
	addresses: string[]
): Promise<ImageryCsvRowResult[]> {
	const res = await fetch(`${API_BASE_URL}/imagery/address/validate-csv`, {
		method: "POST",
		headers: headersJson(enrichmentPublicEntityId),
		body: JSON.stringify({ addresses }),
	});
	if (!res.ok) {
		throw new Error("CSV address validation failed");
	}
	return (await res.json()) as ImageryCsvRowResult[];
}

/** See docs/IMAGERY_SPREADSHEET_ADDRESS_VALIDATION_UI.md */
export interface ImagerySpreadsheetGeocoded {
	address: string;
	city: string;
	state: string;
	county: string;
	zip: string;
	lat: number;
	lng: number;
	place_name?: string;
}

export interface ImagerySpreadsheetRow {
	row_number: number;
	canonical: Record<string, string>;
	ai_full_address: string | null;
	geocoded: ImagerySpreadsheetGeocoded | null;
	error: string | null;
}

export interface ImagerySpreadsheetValidationResponse {
	format: "csv" | "xlsx";
	row_count: number;
	column_mapping: Record<string, string>;
	rows: ImagerySpreadsheetRow[];
}

/**
 * Upload CSV or XLSX for server-side column mapping + geocoding.
 * Do not set Content-Type manually — browser sets multipart boundary.
 */
export async function validateImagerySpreadsheet(
	enrichmentPublicEntityId: string,
	file: File
): Promise<ImagerySpreadsheetValidationResponse> {
	const form = new FormData();
	form.append("file", file, file.name);

	const res = await fetch(
		`${API_BASE_URL}/imagery/address/validate-spreadsheet`,
		{
			method: "POST",
			headers: {
				[HEADER_PUBLIC_ENTITY]: enrichmentPublicEntityId,
			},
			body: form,
		}
	);

	if (!res.ok) {
		const err = (await res.json().catch(() => ({}))) as {
			message?: string;
		};
		throw new Error(
			err.message || res.statusText || "Spreadsheet validation failed"
		);
	}

	return (await res.json()) as ImagerySpreadsheetValidationResponse;
}

export type ImageryLineItem =
	| { real_estate_id: string }
	| {
			real_estate_id?: string;
			address: string;
			city: string;
			state: string;
			county?: string;
			zip?: string;
			lat?: number;
			lng?: number;
	  };

export async function createImageryCheckoutSessionAuth(params: {
	email: string;
	successUrl: string;
	cancelUrl: string;
	lineItems: ImageryLineItem[];
}): Promise<{ url: string; order_id: string }> {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const res = await fetch(`${API_BASE_URL}/imagery/checkout/session-auth`, {
		method: "POST",
		headers: {
			"Content-Type": "application/json",
			Authorization: `Bearer ${token}`,
		},
		body: JSON.stringify({
			email: params.email,
			successUrl: params.successUrl,
			cancelUrl: params.cancelUrl,
			lineItems: params.lineItems,
		}),
	});

	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message ||
				"Failed to create authenticated checkout session"
		);
	}

	const json = (await res.json()) as { url?: string; order_id?: string };
	if (!json.url) {
		throw new Error("Checkout session did not return a URL");
	}

	return {
		url: json.url,
		order_id: json.order_id ?? "",
	};
}

export async function createImageryCheckoutSession(params: {
	enrichmentPublicEntityId: string;
	email: string;
	successUrl: string;
	cancelUrl: string;
	lineItems: ImageryLineItem[];
}): Promise<{ url: string; order_id: string }> {
	const res = await fetch(`${API_BASE_URL}/imagery/checkout/session`, {
		method: "POST",
		headers: headersJson(params.enrichmentPublicEntityId),
		body: JSON.stringify({
			email: params.email,
			successUrl: params.successUrl,
			cancelUrl: params.cancelUrl,
			lineItems: params.lineItems,
		}),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message ||
				"Failed to create checkout session"
		);
	}
	const json = (await res.json()) as { url?: string; order_id?: string };
	if (!json.url) {
		throw new Error("Checkout session did not return a URL");
	}
	return {
		url: json.url,
		order_id: json.order_id ?? "",
	};
}

/** Paginated list from GET /imagery/orders (public entity). */
export interface ImageryOrderPublic {
	id: string;
	created_at?: string;
	email?: string;
	amount_total_cents?: number;
	currency?: string;
	stripe_payment_status?: string;
	requests?: Array<{
		id?: number;
		refunds?: ImageryRefundPublic[];
		[key: string]: unknown;
	}>;
	[key: string]: unknown;
}

/** Nested shapes from GET /imagery/refunds */
export interface ImageryRefundNestedOrder {
	id: string;
	stripe_payment_status?: string;
	amount_total_cents?: number;
	currency?: string;
	email?: string;
	created_at?: string;
}

export interface ImageryRefundNestedPropertyMediaRequest {
	id?: number;
	metadata?: {
		address?: string;
		city?: string;
		state?: string;
		zip?: string;
	};
}

export interface ImageryRefundPublic {
	id: string;
	order_id?: string;
	property_media_request_id?: number | null;
	amount_cents: number;
	currency?: string;
	status: string;
	initiated_by?: string;
	created_at: string;
	updated_at?: string;
	stripe_refund_id?: string | null;
	order?: ImageryRefundNestedOrder;
	property_media_request?: ImageryRefundNestedPropertyMediaRequest | null;
}

export interface ImageryOrdersPaginatedResponse {
	data: ImageryOrderPublic[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

function normalizeImageryOrdersResponse(
	body: unknown,
	fallbackPage: number,
	fallbackPageSize: number
): ImageryOrdersPaginatedResponse {
	if (Array.isArray(body)) {
		const data = body as ImageryOrderPublic[];
		return {
			data,
			total: data.length,
			page: 1,
			pageSize: data.length || fallbackPageSize,
			totalPages: 1,
		};
	}
	if (
		body &&
		typeof body === "object" &&
		Array.isArray((body as ImageryOrdersPaginatedResponse).data)
	) {
		const b = body as ImageryOrdersPaginatedResponse;
		const total = Number(b.total) || b.data.length;
		const pageSize = Number(b.pageSize) || fallbackPageSize;
		return {
			data: b.data,
			total,
			page: Number(b.page) || fallbackPage,
			pageSize,
			totalPages:
				Number(b.totalPages) ||
				Math.max(1, Math.ceil(total / pageSize)),
		};
	}
	return {
		data: [],
		total: 0,
		page: fallbackPage,
		pageSize: fallbackPageSize,
		totalPages: 1,
	};
}

export async function getImageryOrdersPublic(
	enrichmentPublicEntityId: string,
	params?: { page?: number; pageSize?: number }
): Promise<ImageryOrdersPaginatedResponse> {
	const page = params?.page ?? 1;
	const pageSize = params?.pageSize ?? 20;
	const q = new URLSearchParams({
		page: String(page),
		pageSize: String(pageSize),
	});
	const res = await fetch(`${API_BASE_URL}/imagery/orders?${q}`, {
		method: "GET",
		headers: { [HEADER_PUBLIC_ENTITY]: enrichmentPublicEntityId },
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message || "Failed to load orders"
		);
	}
	const body = await res.json();
	return normalizeImageryOrdersResponse(body, page, pageSize);
}

/** Fetch every page of orders (for refund aggregation when no refunds API). */
export async function fetchAllImageryOrdersPublic(
	enrichmentPublicEntityId: string,
	pageSize = 100
): Promise<ImageryOrderPublic[]> {
	const all: ImageryOrderPublic[] = [];
	let page = 1;
	let totalPages = 1;
	do {
		const r = await getImageryOrdersPublic(enrichmentPublicEntityId, {
			page,
			pageSize,
		});
		all.push(...r.data);
		totalPages = r.totalPages;
		page += 1;
	} while (page <= totalPages && totalPages > 0);
	return all;
}

export function aggregateRefundsFromOrders(
	orders: ImageryOrderPublic[]
): ImageryRefundPublic[] {
	const seen = new Set<string>();
	const out: ImageryRefundPublic[] = [];
	for (const order of orders) {
		const oid = String(order.id || "");
		for (const req of order.requests || []) {
			const refunds = req.refunds;
			if (!Array.isArray(refunds)) continue;
			for (const raw of refunds) {
				if (!raw || typeof raw !== "object") continue;
				const r = raw as unknown as Record<string, unknown>;
				const id = String(r.id ?? "");
				if (!id || seen.has(id)) continue;
				seen.add(id);
				out.push({
					id,
					order_id:
						typeof r.order_id === "string"
							? r.order_id
							: oid || undefined,
					property_media_request_id:
						typeof r.property_media_request_id === "number"
							? r.property_media_request_id
							: r.property_media_request_id === null
								? null
								: undefined,
					amount_cents:
						typeof r.amount_cents === "number" ? r.amount_cents : 0,
					currency:
						typeof r.currency === "string" ? r.currency : undefined,
					status: String(r.status || ""),
					initiated_by:
						typeof r.initiated_by === "string"
							? r.initiated_by
							: undefined,
					created_at: String(r.created_at || ""),
					updated_at:
						typeof r.updated_at === "string"
							? r.updated_at
							: undefined,
					stripe_refund_id:
						typeof r.stripe_refund_id === "string"
							? r.stripe_refund_id
							: null,
				});
			}
		}
	}
	out.sort((a, b) => {
		const t =
			new Date(b.created_at).getTime() - new Date(a.created_at).getTime();
		return Number.isNaN(t) ? 0 : t;
	});
	return out;
}

export interface ImageryRefundsPaginatedResponse {
	data: ImageryRefundPublic[];
	total: number;
	page: number;
	pageSize: number;
	totalPages: number;
}

function num(v: unknown, fallback: number): number {
	const n = Number(v);
	return Number.isFinite(n) ? n : fallback;
}

/**
 * Refunds: tries GET /imagery/refunds?page&pageSize (if backend supports it);
 * otherwise aggregates from all orders (paginated fetch server-side per order page).
 */
export async function getImageryRefundsPublic(
	enrichmentPublicEntityId: string,
	params?: { page?: number; pageSize?: number }
): Promise<ImageryRefundsPaginatedResponse> {
	const page = params?.page ?? 1;
	const pageSize = params?.pageSize ?? 20;
	const q = new URLSearchParams({
		page: String(page),
		pageSize: String(pageSize),
	});
	const res = await fetch(`${API_BASE_URL}/imagery/refunds?${q}`, {
		method: "GET",
		headers: { [HEADER_PUBLIC_ENTITY]: enrichmentPublicEntityId },
	});
	if (res.ok) {
		const body = await res.json();
		if (Array.isArray(body)) {
			const data = body as ImageryRefundPublic[];
			const start = (page - 1) * pageSize;
			const slice = data.slice(start, start + pageSize);
			return {
				data: slice,
				total: data.length,
				page,
				pageSize,
				totalPages: Math.max(1, Math.ceil(data.length / pageSize)),
			};
		}
		if (
			body &&
			typeof body === "object" &&
			Array.isArray((body as { data?: unknown[] }).data)
		) {
			const b = body as {
				data: ImageryRefundPublic[];
				total?: unknown;
				page?: unknown;
				pageSize?: unknown;
				totalPages?: unknown;
			};
			const total = num(b.total, b.data.length);
			const ps = num(b.pageSize, pageSize);
			return {
				data: b.data,
				total,
				page: num(b.page, page),
				pageSize: ps,
				totalPages:
					num(b.totalPages, 0) ||
					Math.max(1, Math.ceil(total / (ps || 1))),
			};
		}
	}
	if (res.status !== 404) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message || "Failed to load refunds"
		);
	}
	const orders = await fetchAllImageryOrdersPublic(
		enrichmentPublicEntityId,
		100
	);
	const all = aggregateRefundsFromOrders(orders);
	const total = all.length;
	const totalPages = Math.max(1, Math.ceil(total / pageSize));
	const start = (page - 1) * pageSize;
	return {
		data: all.slice(start, start + pageSize),
		total,
		page,
		pageSize,
		totalPages,
	};
}

export interface ImageryOrderDocumentsResponse {
	receipt_url?: string | null;
	invoice_pdf_url?: string | null;
	invoice_url?: string | null;
	invoice_id?: string | null;
}

export async function getImageryOrderDocumentsPublic(
	enrichmentPublicEntityId: string,
	orderId: string
): Promise<ImageryOrderDocumentsResponse> {
	const res = await fetch(
		`${API_BASE_URL}/imagery/orders/${encodeURIComponent(orderId)}/documents`,
		{
			method: "GET",
			headers: { [HEADER_PUBLIC_ENTITY]: enrichmentPublicEntityId },
		}
	);
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message ||
				"Failed to load receipt or invoice"
		);
	}
	return (await res.json()) as ImageryOrderDocumentsResponse;
}

export async function createImageryCheckoutSessionByOrderPublic(params: {
	enrichmentPublicEntityId: string;
	order_id: string;
	successUrl: string;
	cancelUrl: string;
}): Promise<{ url: string; order_id?: string }> {
	const res = await fetch(
		`${API_BASE_URL}/imagery/checkout/session-by-order`,
		{
			method: "POST",
			headers: headersJson(params.enrichmentPublicEntityId),
			body: JSON.stringify({
				order_id: params.order_id,
				successUrl: params.successUrl,
				cancelUrl: params.cancelUrl,
			}),
		}
	);
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message ||
				"Could not start checkout for this order"
		);
	}
	const json = (await res.json()) as { url?: string; order_id?: string };
	if (!json.url) throw new Error("Checkout did not return a URL");
	return { url: json.url, order_id: json.order_id };
}

export async function syncImageryCheckoutSessionPublic(
	enrichmentPublicEntityId: string,
	sessionId: string
): Promise<unknown> {
	const res = await fetch(`${API_BASE_URL}/imagery/checkout/sync-session`, {
		method: "POST",
		headers: headersJson(enrichmentPublicEntityId),
		body: JSON.stringify({ session_id: sessionId }),
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message ||
				"Could not confirm payment — try refreshing"
		);
	}
	return await res.json();
}

/** GET /imagery/requests — scoped by public entity header */
export async function getImageryRequests(
	enrichmentPublicEntityId: string
): Promise<unknown[]> {
	const res = await fetch(`${API_BASE_URL}/imagery/requests`, {
		method: "GET",
		headers: { [HEADER_PUBLIC_ENTITY]: enrichmentPublicEntityId },
	});
	if (!res.ok) {
		throw new Error("Failed to load imagery requests");
	}
	const data = await res.json();
	return Array.isArray(data) ? data : [];
}

/** GET /imagery/orders/:id — scoped by public entity header */
export async function getImageryOrder(
	enrichmentPublicEntityId: string,
	orderId: string
): Promise<unknown> {
	const res = await fetch(`${API_BASE_URL}/imagery/orders/${orderId}`, {
		method: "GET",
		headers: { [HEADER_PUBLIC_ENTITY]: enrichmentPublicEntityId },
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message || "Failed to load order"
		);
	}
	return await res.json();
}

/** GET /imagery/orders-auth/:id — authenticated user */
export async function getImageryOrderAuth(orderId: string): Promise<unknown> {
	const token = await getAccessToken();
	if (!token) {
		throw new Error("Not authenticated");
	}

	const res = await fetch(`${API_BASE_URL}/imagery/orders-auth/${orderId}`, {
		method: "GET",
		headers: { Authorization: `Bearer ${token}` },
	});

	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message || "Failed to load order"
		);
	}

	return await res.json();
}

/** GET /imagery/orders-auth — orders whose requests all belong to the logged-in user */
export async function getImageryOrdersAuth(): Promise<unknown[]> {
	const token = await getAccessToken();
	if (!token) throw new Error("Not authenticated");
	const res = await fetch(`${API_BASE_URL}/imagery/orders-auth`, {
		method: "GET",
		headers: { Authorization: `Bearer ${token}` },
	});
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message || "Failed to load orders"
		);
	}
	const data = await res.json();
	if (Array.isArray(data)) return data;
	if (
		data &&
		typeof data === "object" &&
		Array.isArray((data as { data?: unknown[] }).data)
	) {
		return (data as { data: unknown[] }).data;
	}
	return [];
}

/** POST /imagery/checkout/session-by-order-auth — pay an existing unpaid order */
export async function createImageryCheckoutSessionByOrderAuth(params: {
	order_id: string;
	successUrl: string;
	cancelUrl: string;
}): Promise<{ url: string }> {
	const token = await getAccessToken();
	if (!token) throw new Error("Not authenticated");
	const res = await fetch(
		`${API_BASE_URL}/imagery/checkout/session-by-order-auth`,
		{
			method: "POST",
			headers: {
				"Content-Type": "application/json",
				Authorization: `Bearer ${token}`,
			},
			body: JSON.stringify({
				order_id: params.order_id,
				successUrl: params.successUrl,
				cancelUrl: params.cancelUrl,
			}),
		}
	);
	if (!res.ok) {
		const err = await res.json().catch(() => ({}));
		throw new Error(
			(err as { message?: string }).message ||
				"Failed to start checkout for this order"
		);
	}
	const json = (await res.json()) as { url?: string };
	if (!json.url) throw new Error("Checkout did not return a URL");
	return { url: json.url };
}
